import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { PrintQrModal } from "@/components/PrintQrModal";
import { RequirePermission } from "@/components/RequirePermission";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { adminStationsApi, stagesApi } from "@/lib/api";
import { displayName } from "@/lib/format";
import type { StationCreate } from "@/lib/types";

const INPUT_CLS = "w-full px-3 py-2 rounded-md bg-surface border border-border";

export default function StationsList() {
  const { t } = useTranslation();
  const [active, setActive] = useState<"all" | "yes" | "no">("all");
  const [q, setQ] = useState("");
  const [openCreate, setOpenCreate] = useState(false);

  const query = useQuery({
    queryKey: ["admin", "stations", { active, q }],
    queryFn: () =>
      adminStationsApi.list({
        is_active: active === "all" ? undefined : active === "yes",
        q: q.trim() || undefined,
        page_size: 200,
      }),
  });

  const stages = useQuery({
    queryKey: ["stages", "all"],
    queryFn: () => stagesApi.list({ page_size: 100 }),
    staleTime: 60_000,
  });

  const stageName = (id: number | null) => {
    if (id == null) return "—";
    const s = stages.data?.items.find((x) => x.id === id);
    return s ? `${s.code} · ${displayName(s)}` : `#${id}`;
  };

  return (
    <RequirePermission codes="station.manage">
      <PageHeader
        eyebrow="admin · stations"
        title={t("stations.title")}
        description={t("stations.description")}
        actions={
          <button className="btn btn-primary" onClick={() => setOpenCreate(true)}>
            {t("stations.newStation")}
          </button>
        }
      />

      <div className="card p-4 flex flex-wrap gap-4 items-end">
        <label className="block text-sm">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{t("stations.activeFilter")}</span>
          <select
            value={active}
            onChange={(e) => setActive(e.target.value as "all" | "yes" | "no")}
            className="mt-1 px-3 py-2 rounded-md bg-surface border border-border font-mono text-xs"
          >
            <option value="all">{t("stations.allFilter")}</option>
            <option value="yes">{t("stations.onlyActive")}</option>
            <option value="no">{t("stations.onlyDisabled")}</option>
          </select>
        </label>
        <label className="block text-sm flex-1 min-w-[12rem]">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            {t("stations.searchLabel")}
          </span>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className={INPUT_CLS}
            placeholder="STAN_3"
          />
        </label>
      </div>

      {query.isLoading ? (
        <LoadingState />
      ) : query.isError ? (
        <ErrorState error={query.error} />
      ) : query.data!.items.length === 0 ? (
        <EmptyState title={t("stations.empty")} hint={t("stations.emptyHint")} />
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left text-text-muted font-mono text-xs uppercase">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">{t("stations.colName")}</th>
                <th className="px-4 py-3">{t("stations.colStage")}</th>
                <th className="px-4 py-3">{t("stations.colStatus")}</th>
                <th className="px-4 py-3 text-right">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {query.data!.items.map((s) => (
                <tr key={s.id} className="border-t border-border hover:bg-surface-hover">
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/stations/${s.id}`}
                      className="font-mono text-accent-strong hover:underline"
                    >
                      {s.code}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-text-secondary">{s.name}</td>
                  <td className="px-4 py-3 text-text-muted text-xs">{stageName(s.stage_id)}</td>
                  <td className="px-4 py-3">
                    <span className={`pill ${s.is_active ? "pill-ok" : ""}`}>
                      {s.is_active ? t("stations.enabled") : t("stations.disabled")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <PrintQrButton stationId={s.id} stationCode={s.code} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openCreate && (
        <CreateStationDialog
          stages={stages.data?.items ?? []}
          onClose={() => setOpenCreate(false)}
        />
      )}
    </RequirePermission>
  );
}

function CreateStationDialog({
  stages,
  onClose,
}: {
  stages: { id: number; code: string; name_ru: string; name?: string }[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [data, setData] = useState<StationCreate>({
    code: "",
    name: "",
    stage_id: null,
    is_active: true,
  });
  const [err, setErr] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: () => adminStationsApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "stations"] });
      onClose();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : t("common.error")),
  });

  function patch<K extends keyof StationCreate>(k: K, v: StationCreate[K]) {
    setData((d) => ({ ...d, [k]: v }));
  }

  return (
    <div className="fixed inset-0 bg-page/70 backdrop-blur z-20 flex items-center justify-center p-6">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          m.mutate();
        }}
        className="card p-6 w-full max-w-md space-y-4 max-h-[90vh] overflow-y-auto"
      >
        <header className="flex items-center justify-between gap-4">
          <h2 className="text-xl">{t("stations.modalTitle")}</h2>
          <button type="button" className="btn text-xs" onClick={onClose}>
            ×
          </button>
        </header>

        <Field label={t("stations.codeLabel")} required>
          <input
            className={`${INPUT_CLS} font-mono`}
            value={data.code}
            onChange={(e) => patch("code", e.target.value.toUpperCase())}
            placeholder="STAN_3"
            autoCapitalize="characters"
          />
        </Field>
        <Field label={t("stations.nameLabel")} required>
          <input
            className={INPUT_CLS}
            value={data.name}
            onChange={(e) => patch("name", e.target.value)}
            placeholder={t("stations.namePlaceholder")}
          />
        </Field>
        <Field label={t("stations.stageLabel")}>
          <select
            className={`${INPUT_CLS} font-mono`}
            value={data.stage_id ?? ""}
            onChange={(e) =>
              patch("stage_id", e.target.value ? Number(e.target.value) : null)
            }
          >
            <option value="">{t("stations.stageNotSet")}</option>
            {stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} · {displayName(s)}
              </option>
            ))}
          </select>
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={data.is_active ?? true}
            onChange={(e) => patch("is_active", e.target.checked)}
          />
          {t("stations.activeCheckbox")}
        </label>

        {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
          <button
            type="button"
            className="btn"
            onClick={onClose}
            disabled={m.isPending}
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={m.isPending || !data.code || !data.name}
          >
            {m.isPending ? t("common.creating") : t("common.create")}
          </button>
        </div>
      </form>
    </div>
  );
}

function PrintQrButton({ stationId, stationCode }: { stationId: number; stationCode: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="btn text-xs"
        onClick={() => setOpen(true)}
        title={t("stations.printQrTitle", { code: stationCode })}
      >
        🖨 QR
      </button>
      {open && (
        <PrintQrModal
          templateKind="station-qr"
          subjectLabel={stationCode}
          onClose={() => setOpen(false)}
          onPrint={(target) => adminStationsApi.printQr(stationId, target)}
        />
      )}
    </>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
        {label}
        {required && <span className="text-danger ml-1">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
